import { z } from 'zod';

import { appearanceSchema, DEFAULT_APPEARANCE } from '@/lib/storefront/appearance';

const text = (max: number) => z.string().max(max);
const link = z.string().max(200).refine(
  (value) => value === '' || value.startsWith('/') && !value.startsWith('//') || /^https?:\/\//.test(value),
  'Links must be a site path or an http(s) URL',
);

export const storefrontDocumentSchema = z.object({
  hero: z.object({
    headline: text(160),
    description: text(600),
    ctaLabel: text(40),
    ctaHref: link,
    imageUrl: z.string().max(2000),
  }),
  featuredVehicleIds: z.array(z.string().max(64)).max(12),
  categories: z.array(z.object({ title: text(240), description: text(400) })).max(8),
  services: z.array(z.object({ title: text(240), description: text(400) })).max(8),
  benefits: z.array(z.object({ title: text(240), description: text(400) })).max(8),
  testimonials: z.array(z.object({ quote: text(400), author: text(80) })).max(8),
  // Multi-day offers shown by designs that declare a "Travel packages" section.
  packages: z
    .array(
      z.object({
        title: text(120).catch(''),
        description: text(400).catch(''),
        duration: text(40).catch(''),
        price: text(40).catch(''),
        imageUrl: z.string().max(2000).catch(''),
      }),
    )
    .max(8)
    .catch([]),
  hours: z.array(z.object({ label: text(40), value: text(80) })).max(7),
  social: z.object({
    facebook: link,
    instagram: link,
    x: link,
    youtube: link,
  }),
  contact: z.object({
    headline: text(160),
    description: text(600),
  }),
  faq: z.array(z.object({ question: text(160), answer: text(600) })).max(12),
  terms: text(8000),
  privacy: text(8000),
  // Older documents predate the appearance options; the catch fills them in.
  appearance: appearanceSchema.catch(DEFAULT_APPEARANCE),
}).refine((document) => !JSON.stringify(document).includes('<'), 'Storefront text cannot contain HTML');

export type StorefrontDocument = z.infer<typeof storefrontDocumentSchema>;

export const EMPTY_STOREFRONT: StorefrontDocument = {
  hero: {
    headline: '',
    description: '',
    ctaLabel: 'Book',
    ctaHref: '/vehicles',
    imageUrl: '',
  },
  featuredVehicleIds: [],
  categories: [],
  services: [],
  benefits: [],
  testimonials: [],
  packages: [],
  hours: [],
  social: { facebook: '', instagram: '', x: '', youtube: '' },
  contact: { headline: '', description: '' },
  faq: [],
  terms: '',
  privacy: '',
  appearance: DEFAULT_APPEARANCE,
};

export function parseStorefront(value: unknown): StorefrontDocument | null {
  const parsed = storefrontDocumentSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}
